import type {
	SignerIFrameEventName,
	SignerInputEvent,
	SignerOutputEvent,
} from "@crossmint/client-signers";
import { decodeBytes } from "open-signer-shared-cryptography";
import type { CrossmintFrameServices } from "..";
import { CrossmintAPIError } from "../api/api";
import { CrossmintHttpError } from "../api/request";
import { ClientFacingError } from "../common/errors";
import { secureWipe } from "../common/secure-memory";
import { collectSafeDebugState, measureFunctionTime } from "../common/utils";
import type { FrameLogger } from "../logger";
import { FatalFrameError } from "../storage/indexed-db";

const DEFAULT_TIMEOUT_MS = 30_000;
type SuccessfulOutputEvent<EventName extends SignerIFrameEventName> = Extract<
	SignerOutputEvent<EventName>,
	{ status: "success" }
>;

export { ClientFacingError } from "../common/errors";

export abstract class EventHandler<
	EventName extends SignerIFrameEventName = SignerIFrameEventName,
> {
	abstract event: `request:${EventName}`;
	abstract responseEvent: `response:${EventName}`;

	abstract handler(
		payload: SignerInputEvent<EventName>,
	): Promise<SuccessfulOutputEvent<EventName>>;

	abstract log_prefix: `[${EventName}:EventHandler]`;

	constructor(
		protected readonly services: CrossmintFrameServices,
		protected readonly logger: FrameLogger,
	) {}

	async callback(
		payload: SignerInputEvent<EventName>,
	): Promise<SignerOutputEvent<EventName>> {
		try {
			this.logger.info(`Device key vault request ${this.event} initiated`);

			const { result, time } = await measureFunctionTime(async () =>
				this.handler(payload),
			);

			this.logger.info(
				`Device key vault request ${this.event} complete after ${time}ms`,
			);
			return result;
		} catch (error: unknown) {
			if (error instanceof FatalFrameError) {
				this.logger.error(
					`Fatal IndexedDB error: ${error.message}`,
					error.cause,
				);
				return {
					status: "error",
					error: "IndexedDB connection failed after retries",
					code: "indexeddb-fatal",
				};
			}

			const debugInfo = await collectSafeDebugState(this.services);
			this.logger.error(
				`Internal handler error: ${error instanceof Error ? error.message : JSON.stringify(error)}.\nEvent: ${this.event}.\nDebug: ${JSON.stringify(debugInfo, undefined, 2)}`,
			);

			// Use client-facing message when explicitly provided, otherwise use generic message
			if (error instanceof ClientFacingError) {
				return {
					status: "error",
					error: error.clientMessage,
					code: error.code,
					data: error.data,
				};
			}

			// Handle API errors that should be shown to the user
			if (error instanceof CrossmintAPIError) {
				return {
					status: "error",
					error: error.clientMessage,
					code: error.code,
					data: error.data,
				};
			}

			return { status: "error", error: "An internal error occurred" };
		}
	}

	options = {
		timeoutMs: DEFAULT_TIMEOUT_MS,
	};
}

export class StartOnboardingEventHandler extends EventHandler<"start-onboarding"> {
	event = "request:start-onboarding" as const;
	responseEvent = "response:start-onboarding" as const;
	log_prefix = "[start-onboarding:EventHandler]" as const;

	async handler(
		payload: SignerInputEvent<"start-onboarding">,
	): Promise<SuccessfulOutputEvent<"start-onboarding">> {
		const deviceId = await this.services.device.getId();
		const { authId, channel } = payload.data;
		const masterSecret = await this.services.secret.getMasterSecret(
			deviceId,
			payload.authData,
			authId,
		);

		try {
			if (masterSecret != null) {
				this.logger.info(
					this.log_prefix,
					"Device already onboarded, returning ready",
				);
				const result = {
					status: "success" as const,
					signerStatus: "ready" as const,
					publicKeys:
						await this.services.cryptoKey.getAllPublicKeysFromSeed(
							masterSecret,
						),
				};
				return result;
			}

			this.logger.info(
				this.log_prefix,
				"New device, initiating onboarding via API",
			);
			await this.services.api.startOnboarding(
				{
					authId,
					channel,
					encryptionContext: {
						publicKey:
							await this.services.device.getSerializedIdentityPublicKey(),
					},
					deviceId,
				},
				payload.authData,
			);
			this.services.secret.markOnboardingStarted(deviceId, authId);

			return {
				status: "success",
				signerStatus: "new-device",
			};
		} finally {
			secureWipe(masterSecret);
		}
	}
}

export class CompleteOnboardingEventHandler extends EventHandler<"complete-onboarding"> {
	event = "request:complete-onboarding" as const;
	responseEvent = "response:complete-onboarding" as const;
	log_prefix = "[complete-onboarding:EventHandler]" as const;

	async handler(
		payload: SignerInputEvent<"complete-onboarding">,
	): Promise<SuccessfulOutputEvent<"complete-onboarding">> {
		const deviceId = await this.services.device.getId();
		// complete-onboarding carries no recovery method; it is the one the OTP was requested for.
		const authId = this.services.secret.pendingOnboardingAuthId(deviceId);
		const decryptedOtp = (
			await this.services.encrypt.decryptFPE(
				payload.data.onboardingAuthentication.encryptedOtp
					.split("")
					.map(Number),
			)
		).join("");

		let onboardingResult: Awaited<
			ReturnType<typeof this.services.api.completeOnboarding>
		>;
		try {
			onboardingResult = await this.services.api.completeOnboarding(
				{
					publicKey:
						await this.services.device.getSerializedIdentityPublicKey(),
					onboardingAuthentication: {
						otp: decryptedOtp,
					},
					deviceId,
					authId,
				},
				payload.authData,
			);
		} catch (error) {
			if (error instanceof CrossmintHttpError && error.status === 401) {
				this.logger.warn(
					this.log_prefix,
					"OTP verification failed: Invalid or expired OTP",
				);
				throw new ClientFacingError("Invalid or expired OTP");
			}
			throw error;
		}

		this.services.secret.markOnboardingCompleted(deviceId);
		const masterSecret = await this.services.secret.setMasterSecret(
			deviceId,
			onboardingResult,
			payload.authData,
			authId,
		);

		try {
			return {
				status: "success",
				signerStatus: "ready",
				publicKeys:
					await this.services.cryptoKey.getAllPublicKeysFromSeed(masterSecret),
			};
		} finally {
			secureWipe(masterSecret);
		}
	}
}

export class GetStatusEventHandler extends EventHandler<"get-status"> {
	event = "request:get-status" as const;
	responseEvent = "response:get-status" as const;
	log_prefix = "[get-status:EventHandler]" as const;

	async handler(
		payload: SignerInputEvent<"get-status">,
	): Promise<SuccessfulOutputEvent<"get-status">> {
		const masterSecret = await this.services.secret.getMasterSecret(
			await this.services.device.getId(),
			payload.authData,
			payload.data?.authId,
		);

		try {
			if (masterSecret == null) {
				this.logger.info(this.log_prefix, "Status: new-device");
				return {
					status: "success",
					signerStatus: "new-device",
				};
			}

			this.logger.info(this.log_prefix, "Status: ready");
			return {
				status: "success",
				signerStatus: "ready",
				publicKeys:
					await this.services.cryptoKey.getAllPublicKeysFromSeed(masterSecret),
			};
		} finally {
			secureWipe(masterSecret);
		}
	}
}

export class SignEventHandler extends EventHandler<"sign"> {
	event = "request:sign" as const;
	responseEvent = "response:sign" as const;
	log_prefix = "[sign:EventHandler]" as const;

	async handler(
		payload: SignerInputEvent<"sign">,
	): Promise<SuccessfulOutputEvent<"sign">> {
		const masterSecret = await this.services.secret.getMasterSecret(
			await this.services.device.getId(),
			payload.authData,
			payload.data.authId,
		);

		if (masterSecret == null) {
			throw new ClientFacingError(
				"This device is not onboarded for the selected recovery method",
				"device-not-onboarded",
			);
		}

		let privateKey: Uint8Array | null = null;

		try {
			const { keyType, bytes, encoding } = payload.data;
			this.logger.info(this.log_prefix, `Deriving key (${keyType})`);

			privateKey = await this.services.cryptoKey.getPrivateKeyFromSeed(
				keyType,
				masterSecret,
			);

			this.logger.info(this.log_prefix, "Generating signature");
			const { signature, publicKey } = await this.services.cryptoKey.sign(
				keyType,
				privateKey,
				decodeBytes(bytes, encoding),
				{ responseEncoding: encoding },
			);

			return {
				status: "success",
				signature,
				publicKey,
			};
		} finally {
			secureWipe(privateKey, masterSecret);
		}
	}
}

export const initializeHandlers = (
	services: CrossmintFrameServices,
	logger: FrameLogger,
): EventHandler[] => [
	new CompleteOnboardingEventHandler(services, logger),
	new StartOnboardingEventHandler(services, logger),
	new SignEventHandler(services, logger),
	new GetStatusEventHandler(services, logger),
];
